import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAdminUsersQuery,
  useGetAdminUserDetailQuery,
  useSetAdminUserStatusMutation,
  useGetAdminFeesQuery,
  useMarkAdminFeePaidMutation,
  useMarkAdminFeePeriodPaidMutation,
  usePatchAdminUserSettingsMutation,
  useRefanoutAdminUserMutation,
  useRunCycleAsyncMutation,
  useLoginAsAdminUserMutation,
} from "@/store/api";
import { useAppDispatch } from "@/store";
import { setSession } from "@/store/authSlice";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog } from "@/components/ui/dialog";
import { fmtUsd, fmtTime } from "@/lib/format";
import { toast } from "sonner";

export default function AdminUsers() {
  const users = useGetAdminUsersQuery(undefined, { pollingInterval: 30000 });
  const fees = useGetAdminFeesQuery(undefined, { pollingInterval: 60000 });
  const [setStatus] = useSetAdminUserStatusMutation();
  const [markPaid] = useMarkAdminFeePaidMutation();
  const [markPeriodPaid] = useMarkAdminFeePeriodPaidMutation();
  const [loginAs, { isLoading: loggingIn }] = useLoginAsAdminUserMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [activeUid, setActiveUid] = useState<number | null>(null);

  const onLoginAs = async (id: number, email: string) => {
    if (!confirm(`Sign in as ${email}?\n\nThis swaps your session — to come back to admin you'll need to sign in again.`)) return;
    try {
      const res = await loginAs(id).unwrap();
      dispatch(setSession({ token: res.token, user: res.user }));
      toast.success(`Now browsing as ${res.user.email}`);
      navigate("/", { replace: true });
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Login-as failed";
      toast.error(msg);
    }
  };

  const onToggle = async (id: number, currentStatus: string) => {
    const next = currentStatus === "active" ? "disabled" : "active";
    if (!confirm(`${next === "disabled" ? "Disable" : "Enable"} this user?`)) return;
    try {
      await setStatus({ id, status: next as "active" | "disabled" }).unwrap();
      toast.success(next === "disabled" ? "User disabled" : "User enabled");
    } catch {
      toast.error("Action failed");
    }
  };

  const onMarkPaid = async (id: number) => {
    if (!confirm("Mark this fee accrual as paid?")) return;
    try {
      await markPaid(id).unwrap();
      toast.success("Marked paid");
    } catch {
      toast.error("Action failed");
    }
  };

  const onMarkPeriodPaid = async (id: number, ym: string, fee: number) => {
    if (!confirm(`Mark ${ym} fee of $${fee.toFixed(2)} as paid?`)) return;
    try {
      await markPeriodPaid(id).unwrap();
      toast.success("Period marked paid");
    } catch {
      toast.error("Action failed");
    }
  };

  const totals = {
    outstanding: (users.data ?? []).reduce(
      (s, u) => s + (u.fees?.outstanding_fee ?? 0), 0,
    ),
    n_users: (users.data ?? []).length,
    n_active: (users.data ?? []).filter((u) => u.status === "active").length,
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <p className="text-xs text-muted mt-1">Click any row for the per-user detail view.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 text-sm">
            <Badge tone="muted">{totals.n_users} total</Badge>
            <Badge tone="good">{totals.n_active} active</Badge>
            <Badge tone="info">${totals.outstanding.toFixed(2)} outstanding fees</Badge>
          </div>
          {users.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (users.data ?? []).length === 0 ? (
            <p className="text-sm text-muted">No users yet.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>id</TH>
                  <TH>Email</TH>
                  <TH>Role</TH>
                  <TH>Status</TH>
                  <TH>Keys</TH>
                  <TH className="text-right">Per-order</TH>
                  <TH className="text-right">Max/day</TH>
                  <TH className="text-right">Today</TH>
                  <TH className="text-right">Outstanding</TH>
                  <TH>Joined</TH>
                  <TH></TH>
                </TR>
              </THead>
              <TBody>
                {(users.data ?? []).map((u) => (
                  <TR
                    key={u.id}
                    onClick={() => setActiveUid(u.id)}
                    className="cursor-pointer"
                  >
                    <TD className="text-xs text-muted">{u.id}</TD>
                    <TD className="font-medium">{u.email}</TD>
                    <TD><Badge tone={u.role === "admin" ? "info" : "muted"}>{u.role}</Badge></TD>
                    <TD>
                      <Badge tone={u.status === "active" ? "good" : "warn"}>{u.status}</Badge>
                    </TD>
                    <TD>
                      <Badge tone={u.has_keys ? "good" : "muted"}>
                        {u.has_keys ? "yes" : "no"}
                      </Badge>
                    </TD>
                    <TD className="text-right tabular">{fmtUsd(u.settings?.per_order_usd ?? 0)}</TD>
                    <TD className="text-right tabular">{u.settings?.max_orders_per_day ?? 0}</TD>
                    <TD className="text-right tabular">{u.trades_today ?? 0}</TD>
                    <TD className="text-right tabular">
                      {fmtUsd(u.fees?.outstanding_fee ?? 0)}
                    </TD>
                    <TD className="text-xs text-muted">{fmtTime(u.created_at)}</TD>
                    <TD onClick={(e) => e.stopPropagation()}>
                      {u.role !== "admin" && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost"
                            disabled={loggingIn || u.status !== "active"}
                            onClick={() => onLoginAs(u.id, u.email)}>
                            Login as
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => onToggle(u.id, u.status)}>
                            {u.status === "active" ? "Disable" : "Enable"}
                          </Button>
                        </div>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Fee ledger — 10% of monthly net P&L
            {fees.data && (
              <span className="text-xs text-muted ml-2 font-normal">
                rate {fees.data.rate_pct.toFixed(0)}% ·
                {" "}{(fees.data.periods ?? []).length} period(s) ·
                {" "}{(fees.data.accruals ?? []).length} closes
              </span>
            )}
          </CardTitle>
          <p className="text-xs text-muted mt-1">
            Charged on each calendar month's NET realized P&L (wins − losses).
            Losing months pay nothing. The per-close accruals below the table
            are kept as an audit trail.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {fees.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (fees.data?.periods ?? []).length === 0 ? (
            <p className="text-sm text-muted">No realized P&L yet — no monthly periods to bill.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Period</TH>
                  <TH>User</TH>
                  <TH className="text-right">Net P&L</TH>
                  <TH className="text-right">Fee (10% of net)</TH>
                  <TH>Status</TH>
                  <TH>Computed</TH>
                  <TH></TH>
                </TR>
              </THead>
              <TBody>
                {(fees.data?.periods ?? []).map((r) => {
                  const paid = !!r.paid_at;
                  const billable = (r.fee_usd ?? 0) > 0;
                  return (
                    <TR key={r.id}>
                      <TD className="font-medium">{r.period_ym}</TD>
                      <TD>{r.email ?? `user ${r.user_id}`}</TD>
                      <TD className="text-right tabular">{fmtUsd(r.net_pnl_usd)}</TD>
                      <TD className="text-right tabular font-semibold">
                        {fmtUsd(r.fee_usd)}
                      </TD>
                      <TD>
                        <Badge tone={paid ? "good" : (billable ? "warn" : "muted")}>
                          {paid ? "paid" : (billable ? "outstanding" : "no-fee")}
                        </Badge>
                      </TD>
                      <TD className="text-xs text-muted">{fmtTime(r.computed_at)}</TD>
                      <TD>
                        {billable && !paid && (
                          <Button size="sm" variant="ghost"
                            onClick={() => onMarkPeriodPaid(r.id, r.period_ym, r.fee_usd)}>
                            Mark paid
                          </Button>
                        )}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}

          {(fees.data?.accruals ?? []).length > 0 && (
            <details className="text-xs">
              <summary className="text-muted cursor-pointer">
                Per-close audit ({(fees.data?.accruals ?? []).length} rows)
              </summary>
              <Table>
                <THead>
                  <TR>
                    <TH>id</TH>
                    <TH>User</TH>
                    <TH>Accrued</TH>
                    <TH className="text-right">Realized P&L</TH>
                    <TH className="text-right">Per-close fee (legacy)</TH>
                    <TH></TH>
                  </TR>
                </THead>
                <TBody>
                  {(fees.data?.accruals ?? []).map((r) => (
                    <TR key={r.id}>
                      <TD className="text-xs text-muted">{r.id}</TD>
                      <TD>{r.email ?? `user ${r.user_id}`}</TD>
                      <TD className="text-xs text-muted">{fmtTime(r.accrued_at)}</TD>
                      <TD className="text-right tabular">{fmtUsd(r.realized_pnl_usd)}</TD>
                      <TD className="text-right tabular">{fmtUsd(r.fee_usd)}</TD>
                      <TD>
                        {!r.paid && (
                          <Button size="sm" variant="ghost"
                            onClick={() => onMarkPaid(r.id)}>
                            Mark paid
                          </Button>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </details>
          )}
        </CardContent>
      </Card>

      <UserDetailDialog uid={activeUid} onClose={() => setActiveUid(null)} />
    </div>
  );
}

function UserDetailDialog({ uid, onClose }: { uid: number | null; onClose: () => void }) {
  const detail = useGetAdminUserDetailQuery(uid ?? 0, {
    skip: uid == null, pollingInterval: 15000,
  });
  const [patch, { isLoading: saving }] = usePatchAdminUserSettingsMutation();
  const [refanout, { isLoading: refanouting }] = useRefanoutAdminUserMutation();
  const [runCycle, { isLoading: cycling }] = useRunCycleAsyncMutation();
  const [loginAs, { isLoading: loggingIn }] = useLoginAsAdminUserMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onLoginAs = async () => {
    if (uid == null) return;
    const email = detail.data?.user.email ?? `user ${uid}`;
    if (!confirm(`Sign in as ${email}?\n\nThis swaps your session — to come back to admin you'll need to sign in again.`)) return;
    try {
      const res = await loginAs(uid).unwrap();
      dispatch(setSession({ token: res.token, user: res.user }));
      toast.success(`Now browsing as ${res.user.email}`);
      navigate("/", { replace: true });
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Login-as failed";
      toast.error(msg);
    }
  };

  const [perOrder, setPerOrder] = useState<number>(10);
  const [maxOrders, setMaxOrders] = useState<number>(5);
  const [approvalMode, setApprovalMode] = useState<"manual" | "auto">("manual");
  const [paused, setPaused] = useState<boolean>(false);
  const [personalize, setPersonalize] = useState<boolean>(false);
  const [canRunCycle, setCanRunCycle] = useState<boolean>(false);
  const [dryRun, setDryRun] = useState<boolean>(true);
  const [perUserCycle, setPerUserCycle] = useState<boolean>(false);
  const [startingEquity, setStartingEquity] = useState<string>("");

  useEffect(() => {
    const s = detail.data?.settings;
    if (!s) return;
    setPerOrder(Number(s.per_order_usd ?? 10));
    setMaxOrders(Number(s.max_orders_per_day ?? 5));
    setApprovalMode((s.approval_mode as "manual" | "auto") ?? "manual");
    setPaused(Boolean(s.paused));
    setPersonalize(Boolean(s.personalize_risk_review));
    setCanRunCycle(Boolean(s.can_run_cycle));
    setDryRun(s.dry_run !== false);
    setPerUserCycle(Boolean(s.per_user_cycle_enabled));
    setStartingEquity(s.starting_equity_usd != null ? String(s.starting_equity_usd) : "");
  }, [detail.data?.user.id, detail.data?.settings.per_order_usd,
      detail.data?.settings.max_orders_per_day]);

  const onSave = async () => {
    if (uid == null) return;
    try {
      await patch({
        id: uid,
        patch: {
          per_order_usd: Number(perOrder),
          max_orders_per_day: Number(maxOrders),
          approval_mode: approvalMode,
          paused: paused ? 1 : 0,
          starting_equity_usd: startingEquity === "" ? null : Number(startingEquity),
          personalize_risk_review: personalize ? 1 : 0,
          can_run_cycle: canRunCycle ? 1 : 0,
          dry_run: dryRun ? 1 : 0,
          per_user_cycle_enabled: perUserCycle ? 1 : 0,
        },
      }).unwrap();
      toast.success("Settings saved");
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Save failed";
      toast.error(msg);
    }
  };

  const onRefanout = async () => {
    if (uid == null) return;
    try {
      const res = await refanout(uid).unwrap();
      const status = (res.outcome as { status?: string } | undefined)?.status ?? "ok";
      toast.success(`Re-fanout applied: ${status}`);
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Re-fanout failed";
      toast.error(msg);
    }
  };

  const onRunCycle = async () => {
    try {
      await runCycle().unwrap();
      toast.success("Cycle queued — fan-out will run after the global decision lands.");
    } catch {
      toast.error("Cycle trigger failed");
    }
  };

  const d = detail.data;

  return (
    <Dialog
      open={uid != null}
      onClose={onClose}
      title={d ? `${d.user.email} · #${d.user.id}` : "User detail"}
      size="xl"
    >
      {!d ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="space-y-4 text-sm">
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="primary"
              onClick={() => { onClose(); navigate(`/admin/users/${d.user.id}`); }}
              title="Full per-user dashboard: charts, open positions, recent trades, settings, LLM cost"
            >
              Open full dashboard →
            </Button>
          </div>
          {/* Top KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Role"><Badge tone={d.user.role === "admin" ? "info" : "muted"}>{d.user.role}</Badge></Kpi>
            <Kpi label="Status"><Badge tone={d.user.status === "active" ? "good" : "warn"}>{d.user.status}</Badge></Kpi>
            <Kpi label="Keys"><Badge tone={d.has_keys ? "good" : "muted"}>{d.has_keys ? "yes" : "no"}</Badge></Kpi>
            <Kpi label="Trades today">{d.trades_today}</Kpi>
            <Kpi label="Outstanding fee">{fmtUsd(d.fees.outstanding_fee)}</Kpi>
            <Kpi label="Paid fees">{fmtUsd(d.fees.paid_fee)}</Kpi>
            <Kpi label="Joined"><span className="text-xs">{fmtTime(d.user.created_at)}</span></Kpi>
            <Kpi label="Closes">{d.fees.n_accruals}</Kpi>
          </div>

          {/* Settings editor */}
          <Card>
            <CardHeader><CardTitle>Per-user settings</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Per-order cap (USD)</Label>
                  <Input type="number" step="0.5" value={String(perOrder)}
                    onChange={(e) => setPerOrder(parseFloat(e.target.value || "0"))} />
                </div>
                <div>
                  <Label>Max orders / day</Label>
                  <Input type="number" step="1" value={String(maxOrders)}
                    onChange={(e) => setMaxOrders(parseInt(e.target.value || "0", 10))} />
                </div>
                <div>
                  <Label>Approval mode</Label>
                  <select
                    className="h-9 rounded-md border border-border bg-bg px-3 text-sm w-full"
                    value={approvalMode}
                    onChange={(e) => setApprovalMode(e.target.value as "manual" | "auto")}
                  >
                    <option value="manual">Manual</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>
                <div>
                  <Label>Starting equity (ROI anchor)</Label>
                  <Input type="number" step="1" value={startingEquity}
                    placeholder="—"
                    onChange={(e) => setStartingEquity(e.target.value)} />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="h-4 w-4 accent-accent"
                    checked={paused} onChange={(e) => setPaused(e.target.checked)} />
                  Pause this user
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="h-4 w-4 accent-accent"
                    checked={personalize} onChange={(e) => setPersonalize(e.target.checked)} />
                  Personal AI risk review
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="h-4 w-4 accent-accent"
                    checked={canRunCycle} onChange={(e) => setCanRunCycle(e.target.checked)} />
                  Can run cycle (Run-cycle button)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="h-4 w-4 accent-accent"
                    checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
                  Dry run (paper trading)
                </label>
                <label className="flex items-center gap-2 text-sm col-span-2">
                  <input type="checkbox" className="h-4 w-4 accent-accent"
                    checked={perUserCycle} onChange={(e) => setPerUserCycle(e.target.checked)} />
                  Independent per-user cycle
                  <span className="text-xs text-muted ml-1">
                    (own advisor pass + risk guard; doesn't share admin's decision)
                  </span>
                </label>
              </div>
              <div className="pt-2 flex flex-wrap gap-2">
                <Button variant="primary" size="sm" disabled={saving} onClick={onSave}>
                  {saving ? "Saving…" : "Save settings"}
                </Button>
                <Button variant="ghost" size="sm" disabled={refanouting} onClick={onRefanout}>
                  {refanouting ? "Running…" : "Re-fanout latest decision"}
                </Button>
                <Button variant="ghost" size="sm" disabled={cycling} onClick={onRunCycle}>
                  {cycling ? "Cycling…" : "Run global cycle"}
                </Button>
                {d.user.role !== "admin" && (
                  <Button variant="ghost" size="sm" disabled={loggingIn}
                          onClick={onLoginAs}>
                    {loggingIn ? "Switching…" : "Login as this user"}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted">
                "Re-fanout" re-applies the most recent global decision to this
                user (writes a fresh user_cycles row). "Run global cycle"
                triggers a brand-new cycle for everyone — same as the topbar
                button.
              </p>
            </CardContent>
          </Card>

          {/* Recent user_cycles */}
          <Card>
            <CardHeader>
              <CardTitle>
                Recent cycles
                <span className="text-xs font-normal text-muted ml-2">
                  {d.cycles.length} shown
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {d.cycles.length === 0 ? (
                <p className="text-sm text-muted">No cycle history yet for this user.</p>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>When</TH>
                      <TH>Action</TH>
                      <TH>Symbol</TH>
                      <TH className="text-right">Applied</TH>
                      <TH>Status</TH>
                      <TH>Notes</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {d.cycles.map((c) => (
                      <TR key={c.id}>
                        <TD className="text-xs text-muted">{fmtTime(c.cycle_started_at)}</TD>
                        <TD>{c.action ?? "—"}</TD>
                        <TD>{c.symbol ?? "—"}</TD>
                        <TD className="text-right tabular">
                          {c.applied_size_usd != null ? fmtUsd(c.applied_size_usd) : "—"}
                        </TD>
                        <TD><Badge tone={
                          c.status === "executed" ? "good"
                          : c.status === "queued" ? "info"
                          : c.status === "error" ? "bad"
                          : c.status === "held" ? "muted"
                          : "warn"}>{c.status}</Badge></TD>
                        <TD className="text-xs text-muted truncate max-w-md">
                          {c.notes ?? c.error ?? ""}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Recent trades */}
          <Card>
            <CardHeader>
              <CardTitle>
                Recent trades
                <span className="text-xs font-normal text-muted ml-2">
                  {d.trades.length} shown
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {d.trades.length === 0 ? (
                <p className="text-sm text-muted">No trades for this user yet.</p>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>When</TH>
                      <TH>Action</TH>
                      <TH>Symbol</TH>
                      <TH className="text-right">Size</TH>
                      <TH className="text-right">Filled</TH>
                      <TH className="text-right">Avg px</TH>
                      <TH>Mode</TH>
                      <TH>Source</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {d.trades.map((t) => (
                      <TR key={t.id}>
                        <TD className="text-xs text-muted">{fmtTime(t.executed_at)}</TD>
                        <TD>
                          <Badge tone={t.action === "BUY" ? "good" : "bad"}>{t.action}</Badge>
                        </TD>
                        <TD className="font-medium">{t.symbol}</TD>
                        <TD className="text-right tabular">{fmtUsd(t.size_usd)}</TD>
                        <TD className="text-right tabular">
                          {t.filled_qty ? Number(t.filled_qty).toFixed(6) : "—"}
                        </TD>
                        <TD className="text-right tabular">
                          {t.avg_price ? Number(t.avg_price).toFixed(4) : "—"}
                        </TD>
                        <TD>
                          <Badge tone={t.dry_run ? "warn" : "muted"}>
                            {t.dry_run ? "dry" : "live"}
                          </Badge>
                        </TD>
                        <TD className="text-xs text-muted">{t.source ?? "—"}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </Dialog>
  );
}

function Kpi({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted uppercase tracking-wide mb-1">{label}</div>
      <div className="text-lg font-semibold">{children}</div>
    </div>
  );
}
